import React, { useState } from "react";
import { observer } from "mobx-react";
import { useStores } from "../../Hooks";
import GeneralDialog from "./GeneralDialog";
import CloseIcon from "@mui/icons-material/Close";
import TimelineIcon from "@mui/icons-material/Timeline";
import SharedUtils from "../../serverClientShared/sharedUtils";
import { WorkspaceAnimationUsesIcon } from "../../assets/svg-icons/WorkspaceAnimationUsesIcon";

const RenderButton = ({ text, link, type }) => {
    const [hovered, setHovered] = useState(false);

    const handleClick = () => {
        if (!link) return;
        window.open(link, "_blank");
    };

    return (
        <div
            className="flex justify-between items-center p-2 rounded-menu hover:bg-background-hover w-full"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <div className="flex items-center gap-2" style={{ width: "75%" }}>
                {type === "procedure" ? (
                    <TimelineIcon className="procedure-icon" />
                ) : type === "workspace" ? (
                    <WorkspaceAnimationUsesIcon />
                ) : (
                    <div style={{ width: "46px", height: "23px" }} />
                )}
                <div className="flex items-center truncate">{text}</div>
            </div>

            {type !== "workspace" && (
                <div
                    className="text-xs text-link-normal rounded-menu text-center py-1 transition-opacity duration-200 hover:bg-panels-normal cursor-pointer"
                    style={{ opacity: hovered ? 1 : 0, width: "18%" }}
                    onClick={handleClick}
                >
                    Open location
                </div>
            )}
        </div>
    );
};

const InteractionUsagesModal = observer(() => {
    const { modalStore, generalStore } = useStores();

    const [interactionName, setInteractionName] = useState(
        modalStore.modalData.interactionName || ""
    );

    const trimmedName = interactionName.trim();

    const getNodesUsingInteraction = (
        procedure,
        interactionId
    ) => {
        if (!procedure?.NestedNodes?.length) return [];

        return procedure.NestedNodes.filter((node) =>
            node.NodeInteractionItems?.some(
                (item) => item?.InteractionId === interactionId
            )
        );
    };

    const renderProcedure = (procedure) => {
        const nodesUses = getNodesUsingInteraction(
            procedure,
            modalStore.modalData.interactionId
        );
        const baseUrl = window.location.origin;
        const procedureLink = `${baseUrl}/edit/${generalStore.workspace}/${procedure.ProjectRef}/${procedure.ParentFolder}/${procedure.ProcedureIdentifier}`;
        const nodesArrayString = nodesUses.map((node) => node.Identifier).join(",");

        return (
            <div className="flex flex-col border-t border-nav-normal" style={{ gap: "1px" }} key={procedure.ProcedureIdentifier}>
                <RenderButton
                    text={procedure.ProcedureName}
                    link={`${procedureLink}?nodeId=${nodesArrayString}`}
                    type="procedure"
                />
                {nodesUses.map((node) => (
                    <RenderButton
                        key={node.Identifier}
                        text={`Step: ${node.StepHeader ? SharedUtils.stripKnownHTMLTags(node.StepHeader) : ""}`}
                        link={`${procedureLink}?nodeId=${node.Identifier}`}
                        type="node"
                    />
                ))}
            </div>
        );
    };

    const renderBody = () => {
        const proceduresUsages = modalStore.modalData.proceduresUsages || [];

        return (
            <div
                className="flex flex-col gap-3 p-3 font-sans text-md"
                style={{ width: "500px", height: "452px" }}
            >
                <div className="flex flex-row items-center justify-between w-full">
                    <div className="font-bold" style={{ fontSize: "16px" }}>
                        Animation Usages
                    </div>
                    <CloseIcon
                        style={{
                            color: "var(--main-normal-color)",
                            fontStyle: "normal",
                            cursor: "pointer",
                            fontSize: "14px",
                        }}
                        onClick={() => (modalStore.isInteractionUsagesModalOpen = false)}
                    />
                </div>
                <div className="font-bold text-md">{trimmedName}</div>
                <div className="flex flex-col overflow-y-auto" style={{ height: "400px" }}>
                    {proceduresUsages.length > 0 ? (
                        <>
                            {proceduresUsages.map((procedure) => renderProcedure(procedure))}
                            {modalStore.modalData?.isUsedInChildren && (
                                <RenderButton
                                    text="Used in another workspace"
                                    link={null}
                                    type="workspace"
                                />
                            )}
                        </>
                    ) : (
                        <div>No usages found</div>
                    )}
                </div>
            </div>
        );
    };

    const onEnter = () => setInteractionName(modalStore.modalData.interactionName || "");

    return (
        <GeneralDialog
            openControl="isInteractionUsagesModalOpen"
            className="procedure-option-dialog"
            onEnter={onEnter}
            body={renderBody()}
            isConfirmDisabled
        />
    );
});

export default InteractionUsagesModal;
